/**
 * Sidebar navigation config.
 *
 * Each contextual sidebar section lives in its own module and exports a single
 * builder: `(context) => item | null`. `PRODUCT_AREA_BUILDERS` is the explicit
 * boundary between product areas; builders must not appear in unrelated areas.
 *
 * To add a section: create a module here, then add it only to the product area
 * that owns it. `Sidebar.vue` renders the active area's result unchanged.
 *
 * The `context` object is assembled once by `../useSidebarNavigation.js`; see
 * that file for the full list of values a builder may read. Values are already
 * unwrapped (plain, not refs) - reactivity comes from the `computed` that wraps
 * the whole build.
 *
 * An item is `{ name, label, icon, to, activeOn, children, badgeCount,
 * getterKeys, collapsible, showTreeLine, component }` and is spread onto
 * `SidebarGroup` / `SidebarGroupLeaf`.
 */

import buildConversations from './conversations';
import buildTickets from './tickets';
import buildCases from './cases';
import buildAiAgent from './ai-agent';
import buildTeamInboxes from './team-inboxes';
import buildChannels from './channels';
import buildCaptain from './captain';
import buildContacts from './contacts';
import buildCompanies from './companies';
import buildOpportunities from './opportunities';
import buildReports from './reports';
import buildTravel from './travel';
import buildCampaigns from './campaigns';
import buildPortals from './portals';
import { SETTINGS_BUILDERS } from './settings';

export const PRODUCT_AREA_BUILDERS = {
  // Mirrors the Inbox panel's reading order: the core queues, the tickets
  // lens, the case queue, the Tola AI lens, the team inboxes, then the
  // channel/saved views.
  inbox: [
    buildConversations,
    buildTickets,
    buildCases,
    buildAiAgent,
    buildTeamInboxes,
    buildChannels,
  ],
  ai: [buildCaptain],
  reports: [buildReports],
  travel: [buildTravel],
  customers: [
    buildContacts,
    buildCompanies,
    buildOpportunities,
    buildCampaigns,
  ],
  // Settings is many groups rather than one; see ./settings/index.js. Help
  // Center renders as its own group after them, its links still gated by the
  // portal routes' feature flag and permissions.
  settings: [...SETTINGS_BUILDERS, buildPortals],
};

export const buildSidebarMenuItems = (context, productArea) =>
  (PRODUCT_AREA_BUILDERS[productArea] || [])
    .map(build => build(context))
    .filter(Boolean);
